import React, { useState } from "react";
import "./SingleProductHeader.scss";
import { Button, Typography, Rate, Divider } from "antd";
import {
  ArrowLeftOutlined,
  HeartOutlined,
  HeartFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { ReactComponent as StarIcon } from "../../../../assets/startIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  addFavorite,
  removeFavorite,
} from "../../../../features/favorites/favoritesActions";
import Utils from "../../../../utils";
import RolesHelper from "../../../../common/services/permissions/RolesHelper";

export default function SingleProductHeader({
  token,
  loading,
  product,
  singleProductHeaderRef,
  navigate,
}) {
  const parentCategory = Utils?.findParentCategory(product);
  const pageVisible = RolesHelper?.getPagesVisibility();
  const dispatch = useDispatch();
  const [togglingFavorite, setTogglingFavorite] = useState(false);
  // product?.is_favorite rides the product read but goes stale after a
  // toggle — overrides record the server-confirmed flag of every toggle this
  // session, so they win when present.
  const overrides = useSelector((state) => state?.favorites?.overrides);
  const isFavorite = overrides?.[product?.id] ?? Boolean(product?.is_favorite);
  // product?.rate goes stale the same way after a rating submit — the reviews
  // overrides carry the fresh item average the server returned with the
  // write, so the widget always shows the average (never the own rating).
  const reviewOverride = useSelector((state) => state?.reviews?.overrides)?.[
    product?.id
  ];
  const shownRate = reviewOverride
    ? reviewOverride?.rate ?? 0
    : product?.rate ?? 0;
  const shownReviewsCount = reviewOverride
    ? reviewOverride?.reviews_count ?? 0
    : product?.reviews_count ?? 0;
  // While the product itself is still being fetched there is no id to toggle,
  // so the heart is disabled — mirroring the blanked name/rating above.
  const heartDisabled = loading || !product?.id;
  return (
    <div className="singleProductHeader-cont" ref={singleProductHeaderRef}>
      <div className="singleProductHeader-cont__titleWrapper">
        <Button
          size="small"
          type="text"
          icon={
            <ArrowLeftOutlined
              style={{
                fontSize: "24px",
                color: token?.icons?.whiteIcon,
              }}
            />
          }
          onClick={() => {
            navigate();
          }}
        />
        <Typography.Text
          className="singleProductHeader-cont__titleWrapper__title"
          style={{
            color: token?.Typography?.mainColorText,
          }}>
          {product?.name && !loading ? product?.name : ""}
        </Typography.Text>
      </div>
      <div className="singleProductHeader-cont__detailWrapper">
        <Typography.Text className="singleProductHeader-cont__detailWrapper__text">
          {parentCategory && !loading ? parentCategory?.name : ""}
        </Typography.Text>
        <Divider
          orientation="vertical"
          className="singleProductHeader-cont__detailWrapper__divider"
          style={{
            backgroundColor: token?.Typography?.mainColorText,
          }}
        />
        <Rate
          // Display-only, marketplace-style: the community average with its
          // count. Rating happens in the reviews tab below; half-star steps
          // — averages like 3.5 show a half star, 4.3 snaps to 4.5, the
          // exact value is the number beside.
          allowHalf
          value={loading ? 0 : Utils?.roundRateToHalf(shownRate)}
          disabled
          character={<StarIcon />}
          count={5}
          style={{
            fontSize: "12px",
          }}
        />
        <Typography.Text className="singleProductHeader-cont__detailWrapper__text">
          {!loading
            ? shownReviewsCount
              ? `${shownRate} (${shownReviewsCount})`
              : shownRate
            : 0}
        </Typography.Text>
        {pageVisible?.is_favorites_visible ? (
          <>
            <Divider
              orientation="vertical"
              className="singleProductHeader-cont__detailWrapper__divider"
              style={{
                backgroundColor: token?.Typography?.mainColorText,
              }}
            />
            <Button
              type="text"
              disabled={heartDisabled}
              loading={
                togglingFavorite && {
                  // Brand-colored spinner in the heart's exact slot, so the
                  // icon swap causes no size or tone shift.
                  icon: (
                    <LoadingOutlined
                      spin
                      style={{
                        color: token?.icons?.colorPrimaryIcon,
                      }}
                    />
                  ),
                }
              }
              icon={
                isFavorite ? (
                  <HeartFilled
                    style={{
                      color: token?.icons?.colorPrimaryIcon,
                      opacity: heartDisabled ? 0.5 : 1,
                    }}
                  />
                ) : (
                  <HeartOutlined
                    style={{
                      color: token?.icons?.colorPrimaryIcon,
                      opacity: heartDisabled ? 0.5 : 1,
                    }}
                  />
                )
              }
              onClick={() => {
                const result = dispatch(
                  isFavorite
                    ? removeFavorite(product?.id)
                    : addFavorite(product?.id)
                );
                if (typeof result?.then === "function") {
                  setTogglingFavorite(true);
                  result.then(() => {
                    setTogglingFavorite(false);
                  });
                }
              }}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
